import React from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { catalogColors } from './catalogColors';

interface LanguageCoverageProps {
  count: number;
  total: number;
  // Optional language code — when provided, renders a self-contained chip with language code + % (Topic 5)
  language?: string;
  className?: string;
  showBar?: boolean;
}

export function LanguageCoverage({ count, total, language, className, showBar = true }: LanguageCoverageProps) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const complete = pct >= 100;
  const tealColor = catalogColors.accentTeal;
  const amberColor = catalogColors.amber;
  const currentColor = complete ? tealColor : amberColor;

  if (language) {
    // Self-contained chip: language code + % + bar + count (Topic 5 chip design)
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'inline-flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg border text-xs min-w-[52px]',
                className,
              )}
              style={{ borderColor: currentColor + '50', backgroundColor: currentColor + '08' }}
            >
              <div className="flex items-baseline gap-1">
                <span className="font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">{language}</span>
                <span className="font-bold tabular-nums text-[11px]" style={{ color: currentColor }}>{pct}%</span>
              </div>
              <div className="w-full h-1 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: currentColor }} />
              </div>
              <span className="text-[10px] text-muted-foreground tabular-nums">{count.toLocaleString()}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{count.toLocaleString()} of {total.toLocaleString()} AEO pages translated to {language} — {pct}% complete</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Legacy compact form (used in Monitor table etc.)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('inline-flex flex-col gap-0.5', className)}>
            <span className="text-xs font-medium tabular-nums" style={{ color: currentColor }}>
              {count}/{total}
            </span>
            {showBar && (
              <div className="w-12 h-1 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: currentColor }} />
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{count} of {total} AEO pages — {pct}% complete</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
